<template>
	<section class="main-content benefits">
		<div class="row">
			<div class="columns column12 mobilecolumn">
				<div class="column3 benefit-content">
					<div class="benefit-list">
						<h2 v-if="subtitle" class="subtitle">{{ subtitle }}</h2>
						<h2>{{ title }}</h2>
						<div v-parse-links v-html="content" />
					</div>
					<div class="button-container">
						<book-button
							:roomid="bookingID"
							:promo="packageID"
							href="#"
							class="button button-cta reserve-now"
						>
							{{ bookButtonLabel }}
						</book-button>
					</div>
				</div>
				<div class="column9">
					<div v-if="images.length > 0" class="embla-wrapper">
						<button class="embla-prev" @click="scrollPrev">
							<font-awesome-icon icon="fa-sharp fa-regular fa-chevron-down" size="1x" :class="icon" />
						</button>
						<div ref="emblaNode" class="embla">
							<div class="embla__container">
								<div v-for="(img, index) in images" :key="index" class="embla__slide">
									<picture>
										<source v-if="img.imageWebp" :srcset="img.imageWebp" type="image/webp" />
										<source v-if="img.image" :srcset="img.image" />
										<img class="fade-image" :src="img.image" :alt="img.imageAlt" loading="lazy" />
									</picture>
								</div>
							</div>
						</div>
						<button class="embla__next" @click="scrollNext">
							<font-awesome-icon icon="fa-sharp fa-regular fa-chevron-down" size="1x" :class="icon" />
						</button>
					</div>

					<picture v-else>
						<source v-if="displayImage.imageWebp" :srcset="displayImage.imageWebp" type="image/webp" />
						<source v-if="displayImage.image" :srcset="displayImage.image" />
						<img class="fade-image" :src="displayImage.image" :alt="displayImage.imageAlt" loading="lazy" />
					</picture>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { useEmblaCarousel } from '@/composables/useEmblaCarousel.js';

const props = defineProps({
	title: { type: String, default: '' },
	subtitle: { type: String, default: '' },
	content: { type: String, default: '' },
	bookButtonLabel: { type: String, default: '' },
	bookingID: { type: String, default: '' },
	packageID: { type: String, default: '' },
	benefits: { type: Array, default: () => [] },
	image: {
		type: Object,
		default: () => ({
			image: '',
			imageAlt: '',
			imageWebp: '',
		}),
	},
	images: {
		type: Array,
		default: () => [],
	},
});

const displayImage = computed(() => {
	return props.image?.image
		? props.image
		: props.images[0] || {
				image: '',
				imageAlt: '',
				imageWebp: '',
			};
});

const { emblaNode, scrollNext, scrollPrev } = useEmblaCarousel({ loop: true, align: 'start' }, 3000);
</script>

<style lang="scss" scoped>
/* stylelint-disable */

.embla-wrapper {
	position: relative;
	width: 100%;
	height: 100%;
	overflow: hidden;
}

.embla-prev,
.embla__next {
	position: absolute;
	top: 50%;
	border: none;
	padding: 10px;
	cursor: pointer;
	z-index: 100;
	color: #fff;
	background-color: transparent;

	svg {
		height: 30px;
	}
}

.embla-prev {
	left: 10px;
	rotate: 90deg;
	transform: translateX(-50%);
}

.embla__next {
	right: 10px;
	rotate: -90deg;
	transform: translateX(50%);
}

.embla {
	overflow: hidden;
	width: 100%;
	height: 100%;
	position: a;
}

.embla__container {
	display: flex;
	height: 100%;
}

.embla__slide {
	flex: 0 0 100%;
	position: relative;
	padding: 0;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
}
/* stylelint-enable */

.benefits .row .columns {
	display: flex;

	.column9 {
		padding: 0;
		position: relative;
		overflow: hidden;
	}
}

.benefits .fade-image {
	opacity: 1;
}

.benefits {
	position: relative;
	padding: 0;

	.button {
		padding: 30px;
	}

	.button-container {
		text-align: end;
		width: 100%;
		display: flex;
		justify-content: flex-end;

		a,
		.reserve-now {
			display: flex;
			align-self: flex-end;
			align-items: center;
			justify-content: center;
			margin: 0 !important;
			aspect-ratio: 1;
		}
	}
}

.benefits .row {
	z-index: 2;
	position: relative;
	width: 96%;
	max-width: 1680px;
}

.benefits .benefit-list {
	background: var(--header-color);
	box-shadow: none;
	padding: 60px 50px;

	p,
	h2 {
		color: #fff !important;
		font-size: 36px;
	}

	.subtitle {
		color: var(--subtitle-color);
	}

	:deep(p) {
		color: #fffbf6;
	}

	:deep(h2),
	:deep(h3),
	:deep(h4) {
		color: #fffbf6;
	}
}

.benefits .column9 {
	background: rgb(249 246 242 / 80%);
	box-shadow: none;

	// backdrop-filter: blur(34px);
	padding: 60px 50px;
}

.benefits ul {
	list-style: none;
	font-size: 18px;
	margin: 0 0 20px;
	padding-left: 0;
}

.benefits li {
	position: relative;
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	margin: 20px 0;
	color: #fffbf6;

	span {
		width: calc(100% - 70px);
	}
}

.benefits li::before {
	content: '';
	background: url('~/assets/images/starVectorGray.svg') no-repeat left center;
	background-size: 20px;
	height: 40px;
	width: 40px;
	display: block;
}

:deep(strong) {
	font-weight: 500;
}

:deep(ul) {
	list-style: none;
	font-size: 16px;
	margin: 30px 0;
	padding-left: 0;
}

:deep(li) {
	position: relative;
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	color: #fffbf6;
}

:deep(li::before) {
	content: '';
	padding: 0 20px;
	background: url('~/assets/images/starVectorGray.svg') no-repeat left center;
	background-size: 20px;
	height: 40px;
	display: block;
}

@media (max-width: 1640px) {
	.benefits .column9 {
		margin-right: 0;
	}
}

@media (max-width: 1380px) {
	.benefits .column3 {
		width: 50%;
	}
}

@media (max-width: 920px) {
	.benefits {
		display: flex;
		height: auto;
		flex-flow: row;

		.columns {
			flex-wrap: wrap;
		}

		.column3 {
			width: 100%;
			display: flex;
			flex-flow: row;
			flex-wrap: nowrap;
			box-sizing: border-box;

			.benefit-list {
				flex: 1 1 70%;
				box-sizing: border-box;
				padding: 30px 30px 0;
			}

			.button-container {
				flex: 1 1 30%;
				box-sizing: border-box;
				padding: 0;
				display: flex;
				align-items: center;
				justify-content: center;

				a {
					width: 100%;
				}
			}
		}

		.column9 {
			width: 100% !important;
			height: 500px;
			box-sizing: border-box;
		}
	}
}

@media (max-width: 500px) {
	.benefits {
		ul {
			font-size: 16px;
		}

		.column9 {
			height: 250px;

			.row {
				margin: 0 !important;
				width: 100% !important;
			}
		}

		.row {
			margin: 0 !important;
			width: 100% !important;
		}

		.mobilecolumn {
			padding-left: 0 !important;
			padding-right: 0 !important;
		}
	}
}
</style>
